import React, { ReactElement, useMemo } from 'react';
import StarAnimationWhite from '../assets/animations/StarAnimationWhite.json';
import SuccessModal from './SuccessModal';
import { Trans, useTranslation } from 'react-i18next';
import useCashbackProgram from '../hooks/cashback/useCashbackProgram';
import useAuth from '../hooks/global/useAuth';
import CustomButton from '../components-library/buttons/CustomButton';
import useAppMethods from '../hooks/utility/useAppMethods';
import { Order } from '../services/exports/Interfaces';
import useDeepRef from '../hooks/utility/useDeepRef';
import collect from 'collect.js';

interface Props {
  open: boolean;
  order: Order;
  onCashbackClaimed: () => void;
  toggleModal: () => void;
}

export default function CashbackEarnedModal(props: Props): ReactElement {
  const { open, order, onCashbackClaimed, toggleModal } = props;

  const { t } = useTranslation(null, { keyPrefix: 'Modals:CashbackEarnedModal' });

  const { isLoggedIn, isGuest } = useAuth();
  const { toggleLoginModal } = useAppMethods();
  const { hasCashbackProgram, balance, formattedBalance, formatPointsAsMoney } = useCashbackProgram({
    company: order.company,
  });

  const isGuestRef = useDeepRef(isGuest);

  const onClaimPoints = () => {
    toggleModal();
    toggleLoginModal({
      openModal: true,
      isStatic: true,
      initialPhoneNumber: order.customer_phone_number,
      onCloseRedirect: () => (isGuestRef.current ? null : onCashbackClaimed()),
    });
  };

  const value = useMemo(
    () => +collect(order?.cashback_account_operations).sum('value'),
    [order?.cashback_account_operations],
  );

  const unclaimedValue = useMemo(
    () => +collect(order?.cashback_account_operations).where('is_claimed', false).sum('value'),
    [order?.cashback_account_operations],
  );

  return (
    hasCashbackProgram &&
    value > 0 &&
    (isLoggedIn || unclaimedValue > 0) && (
      <SuccessModal
        open={open}
        animationDescription={<h5 className="text-brand-button-primary-text">{t('title', { value })}</h5>}
        lottieProps={{
          animationData: StarAnimationWhite,
        }}
        buttonTitle={t('buttons.ok')}
        onConfirm={toggleModal}
      >
        {isLoggedIn ? (
          <>
            <h6>
              <Trans
                t={t}
                values={{ value: balance, moneyValue: formattedBalance }}
                components={{
                  highlight: <span className="text-brand-primary" />,
                }}
              >
                balance
              </Trans>
            </h6>
            <p className="text-sm mt-md">{t('description')}</p>
          </>
        ) : (
          <>
            <h6>
              <Trans
                t={t}
                values={{ value, moneyValue: formatPointsAsMoney(value) }}
                components={{
                  highlight: <span className="text-brand-primary" />,
                }}
              >
                guest_description
              </Trans>
            </h6>
            <CustomButton title={t('buttons.claim')} className="!w-fit mt-md" onClick={onClaimPoints} />
          </>
        )}
      </SuccessModal>
    )
  );
}
